import { FC, Fragment, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import {
  BKJCheckboxListItem,
  BKJListItem,
  BKJSelect,
  BKJSelectSearchControls,
} from '@bookjane2/bookjane-design-library';
import { ChangeFunctionType } from '@bookjane2/bookjane-design-library/lib/common.types';
import { IBKJSelectRenderOptionsProps } from '@bookjane2/bookjane-design-library/lib/components/BKJSelect/BKJSelect.types';
import { useTranslation } from 'hooks/useTranslation';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';
import { usePayPeriodStore } from 'pages/ReportsPage/store/payPeriod.store';
import { EmploymentStatusesTypeSelectInput } from 'components/EmploymentStatusesTypeSelectInput';
import {
  PositionByDepartmentResponseDataType,
  PositionByDepartmentSelectInputComponent,
  PositionByDepartmentSelectInputContext,
  PositionByDepartmentSelectInputProvider,
} from 'components/PositionByDepartmentSelectInput';
import { ReportsDateInput } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/ReportsDateInput';
import { PayPeriodDateInput } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/PayPeriodDateInput';
import { isSinglePayPeriodRange } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/PayPeriodDateInput.utils';
import {
  collectPositionOptionIds,
  getDefaultedPositionSelection,
  getFirstPositionOptionId,
} from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/positionSelection.utils';
import {
  getDefaultLocationId,
  isDefaultFilterSelection,
  isDefaultPayPeriodFilterSelection,
} from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/defaultFilters.utils';
import {
  DATE_PRESETS,
  isoToISOStart,
  isoToISOEnd,
  getMinCustomDate,
} from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/FilterBar.constants';
import { useFilterBarLocationData } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/useFilterBarLocationData';
import {
  DateFilterSection,
  DatePresetButton,
  DateRangeSeparator,
  FilterBarContainer,
  FilterSection,
  PayPeriodFilterSection,
  PayPeriodLabel,
  ResetButton,
} from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/FilterBar.styled';

const JANE_TYPE_TO_EMPLOYMENT_STATUS: Record<string, string> = {
  internal_full_time: 'full_time',
  internal_part_time: 'part_time',
  internal_casual: 'casual',
  flex: 'flex',
};

const EMPLOYMENT_STATUS_TO_JANE_TYPE: Record<string, string> = {
  full_time: 'internal_full_time',
  part_time: 'internal_part_time',
  casual: 'internal_casual',
  flex: 'flex',
};

const LocationFilter: FC = () => {
  const { t, i18n } = useTranslation();
  const { locationOptions } = useFilterBarLocationData();
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const setLocationIds = useAnalyticsFilterStore((s) => s.setLocationIds);

  const displayedLocationIds = useMemo(
    () => (locationIds === null ? locationOptions.map(({ value }) => value) : locationIds),
    [locationIds, locationOptions],
  );

  const handleChange: ChangeFunctionType = useCallback(
    (e) => {
      setLocationIds(e.target.value);
    },
    [setLocationIds],
  );

  const RenderOptions: FC<IBKJSelectRenderOptionsProps> = useCallback(
    (props) => {
      const isAllSelected = props.selected.length === locationOptions.length;

      return (
        <Fragment>
          <BKJSelectSearchControls
            locale={i18n.language}
            numSelected={props.selected.length}
            isAllSelected={isAllSelected}
            onSelectAll={() => {
              isAllSelected
                ? props.onChange({
                    target: {
                      name: 'locations',
                      label: [],
                      value: [],
                    },
                  })
                : props.onChange({
                    target: {
                      name: 'locations',
                      label: locationOptions.map(({ label }) => label),
                      value: locationOptions.map(({ value }) => value),
                    },
                  });
            }}
          />
          <div>
            {locationOptions.map((opt) => {
              const { type, ...restProps } = props;
              return <BKJCheckboxListItem {...restProps} {...opt} key={opt.key} />;
            })}
          </div>
        </Fragment>
      );
    },
    [locationOptions, i18n.language],
  );

  return (
    <FilterSection>
      <BKJSelect
        moreValuesLabel={t('MORE')}
        options={locationOptions}
        type="Multiple"
        variant="GreyOutlinedPillClear"
        placeholder={t('settings:Locations')}
        dropdownWidth="358px"
        dropdownPlacement="bottom-start"
        name="locations"
        onChange={handleChange}
        value={displayedLocationIds}
        renderOptions={RenderOptions}
      />
    </FilterSection>
  );
};

const EmploymentTypeFilter: FC = () => {
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);
  const setEmploymentTypes = useAnalyticsFilterStore((s) => s.setEmploymentTypes);

  const value = useMemo(
    () => employmentTypes.map((type) => EMPLOYMENT_STATUS_TO_JANE_TYPE[type] ?? type),
    [employmentTypes],
  );

  const handleChange: ChangeFunctionType = useCallback(
    (e) => {
      const selected: string[] = e.target.value;
      setEmploymentTypes(selected.map((type) => JANE_TYPE_TO_EMPLOYMENT_STATUS[type] ?? type));
    },
    [setEmploymentTypes],
  );

  return (
    <FilterSection>
      <EmploymentStatusesTypeSelectInput
        name="employmentTypes"
        value={value}
        onChange={handleChange}
      />
    </FilterSection>
  );
};

const MultiPositionSelectInput: FC = () => {
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const setPositions = useAnalyticsFilterStore((s) => s.setPositions);
  const allPositionIds = useAnalyticsFilterStore((s) => s.allPositionIds);
  const setAllPositionIds = useAnalyticsFilterStore((s) => s.setAllPositionIds);
  const { data, values, loadMore, isLoading } = useContext(PositionByDepartmentSelectInputContext);
  const requestedCursorRef = useRef<Nullable<string>>(null);

  const typedData = data as PositionByDepartmentResponseDataType | undefined;
  const searchTerm = String(values?.byName ?? '');
  const hasNextPage = !!typedData?.pageInfo?.hasNextPage;

  useEffect(() => {
    if (searchTerm || isLoading) return;
    if (hasNextPage) {
      const cursor = typedData?.pageInfo?.endCursor ?? '';
      if (requestedCursorRef.current === cursor) return;
      requestedCursorRef.current = cursor;
      loadMore();
      return;
    }
    setAllPositionIds(collectPositionOptionIds(typedData?.options ?? {}));
  }, [typedData, searchTerm, hasNextPage, isLoading, loadMore, setAllPositionIds]);

  const handleChange: ChangeFunctionType = useCallback(
    (e) => {
      const next: Array<string | number> = e.target.value;
      setPositions(next.map(String));
    },
    [setPositions],
  );

  const displayedPositions = positions === null ? allPositionIds ?? [] : positions;

  return (
    <PositionByDepartmentSelectInputComponent
      name="positions"
      value={displayedPositions}
      onChange={handleChange}
    />
  );
};

const PositionFilter: FC = () => {
  const { locationOptions } = useFilterBarLocationData();
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);

  const effectiveLocationIds = useMemo(
    () =>
      locationIds && locationIds.length > 0
        ? locationIds
        : locationOptions.map(({ value }) => value),
    [locationIds, locationOptions],
  );

  return (
    <FilterSection key={effectiveLocationIds.join(',')}>
      <PositionByDepartmentSelectInputProvider locationIds={effectiveLocationIds}>
        <MultiPositionSelectInput />
      </PositionByDepartmentSelectInputProvider>
    </FilterSection>
  );
};

const SingleLocationFilter: FC = () => {
  const { t } = useTranslation();
  const { locationOptions, isLoading } = useFilterBarLocationData();
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);
  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const payPeriodSelectionByLocation = useAnalyticsFilterStore(
    (s) => s.payPeriodSelectionByLocation,
  );
  const setLocationIds = useAnalyticsFilterStore((s) => s.setLocationIds);
  const setPositions = useAnalyticsFilterStore((s) => s.setPositions);
  const setEmploymentTypes = useAnalyticsFilterStore((s) => s.setEmploymentTypes);
  const setDateRange = useAnalyticsFilterStore((s) => s.setDateRange);
  const rememberPayPeriodSelection = useAnalyticsFilterStore((s) => s.rememberPayPeriodSelection);

  const applyLocationSelection = useCallback(
    (nextLocationId: string) => {
      const remembered = payPeriodSelectionByLocation[nextLocationId];
      setLocationIds([nextLocationId]);
      setPositions(remembered?.positions ?? []);
      setEmploymentTypes(remembered?.employmentTypes ?? []);
      if (remembered) setDateRange(remembered.dateRange, 'CUSTOM');
    },
    [payPeriodSelectionByLocation, setLocationIds, setPositions, setEmploymentTypes, setDateRange],
  );

  useEffect(() => {
    if (isLoading || locationOptions.length === 0) return;
    const isValidSelection =
      locationIds?.length === 1 && locationOptions.some((opt) => opt.value === locationIds?.[0]);
    if (isValidSelection) return;
    const defaultLocationId = getDefaultLocationId(locationOptions);
    if (defaultLocationId) applyLocationSelection(defaultLocationId);
  }, [isLoading, locationOptions, locationIds, applyLocationSelection]);

  const handleChange: ChangeFunctionType = useCallback(
    (e) => {
      const next: string[] = e.target.value;
      if (next.length !== 1 || next[0] === locationIds?.[0]) return;
      if (locationIds && locationIds.length === 1 && positions && positions.length === 1) {
        rememberPayPeriodSelection(locationIds[0], { positions, employmentTypes, dateRange });
      }
      applyLocationSelection(next[0]);
    },
    [
      locationIds,
      positions,
      employmentTypes,
      dateRange,
      rememberPayPeriodSelection,
      applyLocationSelection,
    ],
  );

  const RenderOptions: FC<IBKJSelectRenderOptionsProps> = useCallback(
    (props) => {
      const { type, ...restProps } = props;
      return (
        <div>
          {locationOptions.map((opt) => (
            <BKJListItem {...restProps} {...opt} key={opt.key} />
          ))}
        </div>
      );
    },
    [locationOptions],
  );

  return (
    <FilterSection>
      <BKJSelect
        options={locationOptions}
        type="Single"
        variant="GreyOutlinedPill"
        placeholder={t('settings:Locations')}
        dropdownWidth="358px"
        dropdownPlacement="bottom-start"
        name="locations"
        onChange={handleChange}
        value={locationIds ?? []}
        renderOptions={RenderOptions}
      />
    </FilterSection>
  );
};

const SinglePositionAutoSelect: FC = () => {
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const setPositions = useAnalyticsFilterStore((s) => s.setPositions);
  const setDefaultPositionId = useAnalyticsFilterStore((s) => s.setDefaultPositionId);
  const { data, values } = useContext(PositionByDepartmentSelectInputContext);

  useEffect(() => {
    const typedData = data as PositionByDepartmentResponseDataType | undefined;
    const searchTerm = String(values?.byName ?? '');
    if (!searchTerm) setDefaultPositionId(getFirstPositionOptionId(typedData?.options ?? {}));
    const defaultedPosition = getDefaultedPositionSelection({
      positions: positions ?? [],
      options: typedData?.options ?? {},
      hasNextPage: !!typedData?.pageInfo?.hasNextPage,
      searchTerm,
    });
    if (defaultedPosition) setPositions([defaultedPosition]);
  }, [data, values, positions, setPositions, setDefaultPositionId]);

  return null;
};

const SinglePositionFilter: FC = () => {
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const setPositions = useAnalyticsFilterStore((s) => s.setPositions);

  const handleChange: ChangeFunctionType = useCallback(
    (e) => {
      const next: Array<string | number> = e.target.value;
      if (next.length !== 1) return;
      setPositions(next.map(String));
    },
    [setPositions],
  );

  if (!locationIds || locationIds.length !== 1) return null;

  return (
    <FilterSection key={locationIds[0]}>
      <PositionByDepartmentSelectInputProvider locationIds={locationIds}>
        <SinglePositionAutoSelect />
        <PositionByDepartmentSelectInputComponent
          name="positions"
          type="Single"
          variant="GreyOutlinedPill"
          value={positions ?? []}
          onChange={handleChange}
          dropdownProps={{ dropdownListItemVariant: 'List' }}
        />
      </PositionByDepartmentSelectInputProvider>
    </FilterSection>
  );
};

const PayPeriodFilter: FC = () => {
  const { t } = useTranslation();
  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const setDateRange = useAnalyticsFilterStore((s) => s.setDateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const status = usePayPeriodStore((s) => s.status);
  const config = usePayPeriodStore((s) => s.config);
  const currentPeriodRange = usePayPeriodStore((s) => s.currentPeriodRange);
  const payPeriodCommunityId = usePayPeriodStore((s) => s.communityId);
  const payPeriodPositionId = usePayPeriodStore((s) => s.positionId);
  const fetchPayPeriod = usePayPeriodStore((s) => s.fetchPayPeriod);

  const communityId = locationIds && locationIds.length === 1 ? locationIds[0] : null;
  const positionId = positions && positions.length === 1 ? positions[0] : null;

  useEffect(() => {
    if (communityId && positionId) fetchPayPeriod(communityId, positionId);
  }, [communityId, positionId, fetchPayPeriod]);

  const isConfigForSelectedCohort =
    !!communityId &&
    !!positionId &&
    payPeriodCommunityId === communityId &&
    payPeriodPositionId === positionId;

  useEffect(() => {
    if (status !== 'ready' && status !== 'fallback') return;
    if (!isConfigForSelectedCohort || !config || !currentPeriodRange) return;
    if (!isSinglePayPeriodRange(dateRange, config)) {
      setDateRange(currentPeriodRange, 'CUSTOM');
    }
  }, [status, isConfigForSelectedCohort, config, currentPeriodRange, dateRange, setDateRange]);

  return (
    <PayPeriodFilterSection>
      <PayPeriodLabel>{t('reports:PAY_PERIOD')}</PayPeriodLabel>
      <PayPeriodDateInput
        name="payPeriod"
        ariaLabel={t('reports:PAY_PERIOD')}
        value={dateRange}
        payPeriod={config ?? undefined}
        disabledTo={getMinCustomDate()}
        onChange={(range) => setDateRange(range, 'CUSTOM')}
      />
    </PayPeriodFilterSection>
  );
};

const DateRangeFilter: FC = () => {
  const { t } = useTranslation();
  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const datePreset = useAnalyticsFilterStore((s) => s.datePreset);
  const setDatePreset = useAnalyticsFilterStore((s) => s.setDatePreset);
  const setDateRange = useAnalyticsFilterStore((s) => s.setDateRange);

  const isCustom = datePreset === 'CUSTOM';

  return (
    <DateFilterSection>
      {DATE_PRESETS.map((preset) => (
        <DatePresetButton
          key={preset.key}
          isActive={datePreset === preset.key}
          onClick={() => setDatePreset(preset.key)}
          type="button"
        >
          {t(preset.label)}
        </DatePresetButton>
      ))}
      <DatePresetButton
        isActive={isCustom}
        onClick={() => setDateRange(dateRange, 'CUSTOM')}
        type="button"
      >
        {t('reports:CUSTOM')}
      </DatePresetButton>
      {isCustom && (
        <Fragment>
          <ReportsDateInput
            name="customStartDate"
            ariaLabel={t('reports:START_DATE')}
            value={isoToISOStart(dateRange[0])}
            disabledFrom={isoToISOStart(dateRange[1])}
            disabledTo={getMinCustomDate()}
            calendarOffset={[-40, 6]}
            onChange={(iso) => setDateRange([isoToISOStart(iso), dateRange[1]], 'CUSTOM')}
          />
          <DateRangeSeparator>{t('reports:TO')}</DateRangeSeparator>
          <ReportsDateInput
            name="customEndDate"
            ariaLabel={t('reports:END_DATE')}
            value={isoToISOStart(dateRange[1])}
            disabledTo={isoToISOStart(dateRange[0])}
            onChange={(iso) => setDateRange([dateRange[0], isoToISOEnd(iso)], 'CUSTOM')}
          />
        </Fragment>
      )}
    </DateFilterSection>
  );
};

interface FilterBarProps {
  variant?: 'default' | 'payPeriod';
}

const useIsDefaultFiltersSelected = (isPayPeriodVariant: boolean): boolean => {
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);
  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const datePreset = useAnalyticsFilterStore((s) => s.datePreset);
  const defaultPositionId = useAnalyticsFilterStore((s) => s.defaultPositionId);
  const allPositionIds = useAnalyticsFilterStore((s) => s.allPositionIds);
  const { locationOptions } = useFilterBarLocationData();
  const payPeriodCommunityId = usePayPeriodStore((s) => s.communityId);
  const payPeriodPositionId = usePayPeriodStore((s) => s.positionId);
  const currentPeriodRange = usePayPeriodStore((s) => s.currentPeriodRange);

  if (!isPayPeriodVariant) {
    return isDefaultFilterSelection(
      { locationIds, positions, employmentTypes, datePreset },
      {
        allLocationIds: locationOptions.map(({ value }) => value),
        allPositionIds,
      },
    );
  }

  return isDefaultPayPeriodFilterSelection(
    { locationIds, positions, employmentTypes, dateRange, defaultPositionId },
    {
      defaultLocationId: getDefaultLocationId(locationOptions),
      payPeriodCommunityId,
      payPeriodPositionId,
      currentPeriodRange,
    },
  );
};

export const FilterBar: FC<FilterBarProps> = ({ variant = 'default' }) => {
  const { t } = useTranslation();
  const reset = useAnalyticsFilterStore((s) => s.reset);
  const isPayPeriodVariant = variant === 'payPeriod';
  const isDefaultFiltersSelected = useIsDefaultFiltersSelected(isPayPeriodVariant);

  return (
    <FilterBarContainer aria-label={t('reports:FILTER_BAR')}>
      {isPayPeriodVariant ? (
        <Fragment>
          <SingleLocationFilter />
          <SinglePositionFilter />
          <EmploymentTypeFilter />
          <PayPeriodFilter />
        </Fragment>
      ) : (
        <Fragment>
          <LocationFilter />
          <PositionFilter />
          <EmploymentTypeFilter />
          <DateRangeFilter />
        </Fragment>
      )}
      <ResetButton type="button" onClick={reset} disabled={isDefaultFiltersSelected}>
        {t('reports:RESET_FILTERS')}
      </ResetButton>
    </FilterBarContainer>
  );
};
